import React, { useEffect, useMemo, useState } from 'react';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import { format, parseISO } from 'date-fns';

import Calendar from 'components/CustomCalendar';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import SnackBar from 'components/SnackBar';
import { globalBEDateFormat } from 'common/dateFormatConfig';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { hasPermissionsFor } from 'helpers/_helpers';
import { DataTable, PageToolbar } from 'lib';
import {
  fetchGeoLocationUsers,
  fetchGeoLocations,
  geoLocationsActions,
  selectGeoLocationFilters,
  selectGeoLocationRows,
  selectGeoLocationUsers,
  selectGeoLocationsError,
  selectGeoLocationsHasMore,
  selectGeoLocationsLoading,
} from 'store/geoLocations';
import FiltersDrawer from './components/FiltersDrawer';
import GeoCell from './components/GeoCell';
import UserSearch from './components/UserSearch';
import AdminCredentialsDialog from './modals/AdminCredentialsDialog';
import { COLUMNS } from './constants';
import { styles } from './styles';

const useStyles = makeStyles(styles);

const GeoLocationsPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dateFormat, dateFormatWithTime } = useDateSettingsFormat();

  const data = useSelector(selectGeoLocationRows);
  const hasMore = useSelector(selectGeoLocationsHasMore);
  const loading = useSelector(selectGeoLocationsLoading);
  const error = useSelector(selectGeoLocationsError);
  const filters = useSelector(selectGeoLocationFilters);
  const users = useSelector(selectGeoLocationUsers);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => () => dispatch(geoLocationsActions.reset()), []);

  useEffect(() => {
    dispatch(fetchGeoLocationUsers());
  }, [filters.from, filters.to]);

  useEffect(() => {
    dispatch(fetchGeoLocations());
  }, [filters]);

  const dates = useMemo(
    () => [parseISO(filters.from), parseISO(filters.to)],
    [filters.from, filters.to]
  );

  const rows = useMemo(() => {
    const empty = <span className={classes.empty}>&mdash;</span>;
    const formatTime = (time) => (time ? format(new Date(time), dateFormatWithTime) : empty);
    const formatUser = ({ username, employeeNum }) =>
      employeeNum ? `${username} / #${employeeNum}` : username;
    // Mobile Kiosk punches carry the host account; clock in and out may be hosted by different people.
    const formatHosts = ({ inCoordinates, outCoordinates }) => {
      const hosts = [inCoordinates?.hostUsername, outCoordinates?.hostUsername].filter(Boolean);
      return [...new Set(hosts)].join(' / ') || empty;
    };

    return data.map((row) => ({
      key: row.timeclockId,
      cells: [
        { content: formatUser(row) },
        { content: formatHosts(row) },
        { content: formatTime(row.inTime) },
        { content: <GeoCell coordinates={row.inCoordinates} /> },
        { content: formatTime(row.outTime) },
        { content: <GeoCell coordinates={row.outCoordinates} /> },
      ],
    }));
  }, [data, dateFormatWithTime, classes.empty]);

  const selectedUsername = users.find((user) => user.userId === filters.userId)?.username;
  const range = `from ${format(dates[0], dateFormat)} to ${format(dates[1], dateFormat)}`;
  const purgeTarget = `${selectedUsername ? `${selectedUsername}'s` : 'all'} geolocation data ${range}`;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3">GPS Coordinates</Typography>
        <Button variant="contained" color="secondary" onClick={() => navigate('/timeclock')}>
          Time Clock
        </Button>
        <div className={classes.spacer} />
        <Calendar
          dates={dates}
          updateDates={([from, to]) =>
            dispatch(
              geoLocationsActions.filtersChanged({
                from: format(from, globalBEDateFormat),
                to: format(to, globalBEDateFormat),
              })
            )
          }
        />
      </div>

      <PageToolbar
        className={classes.toolbar}
        onFiltersClick={() => setFiltersOpen(true)}
        search={
          <UserSearch
            users={users}
            value={filters.userId}
            onChange={(userId) => dispatch(geoLocationsActions.filtersChanged({ userId }))}
          />
        }
      >
        {hasPermissionsFor('geoLocationsPurge') && (
          <Button
            variant="outlined"
            className={classes.purgeBtn}
            onClick={() => setConfirmOpen(true)}
          >
            Purge Location Data
          </Button>
        )}
      </PageToolbar>
      <FiltersDrawer
        open={filtersOpen}
        value={filters}
        onClose={() => setFiltersOpen(false)}
        onApply={({ sortBy, sortOrder }) => {
          dispatch(geoLocationsActions.filtersChanged({ sortBy, sortOrder }));
          setFiltersOpen(false);
        }}
      />

      <InfiniteScroll
        // Remounts on every filter change so the scroller's page counter restarts with the rows.
        key={`${filters.userId}|${filters.from}|${filters.to}|${filters.sortBy}|${filters.sortOrder}`}
        loadMore={() => dispatch(fetchGeoLocations({ append: true }))}
        hasMore={hasMore && !loading}
        loader={
          <div className={classes.loader} key={0}>
            Loading ...
          </div>
        }
        threshold={400}
      >
        <DataTable columns={COLUMNS} rows={rows} loading={loading} striped dense />
      </InfiniteScroll>

      <ConfirmDialog
        isOpen={confirmOpen}
        variant="primary"
        showClose
        text="Delete geolocation data?"
        description={
          <>
            <span className={classes.descriptionLine}>
              Are you sure you want to delete {purgeTarget}? This action{' '}
              <b>can&apos;t be undone</b>.
            </span>
            <span className={classes.descriptionLine}>
              To continue, you&apos;ll be asked to enter your admin credentials.
            </span>
          </>
        }
        cancelBtn="Cancel"
        confirmBtn="Yes"
        onClose={() => setConfirmOpen(false)}
        onSubmit={() => {
          setConfirmOpen(false);
          setCredentialsOpen(true);
        }}
      />
      {credentialsOpen && <AdminCredentialsDialog onClose={() => setCredentialsOpen(false)} />}

      <SnackBar
        isOpen={!!error}
        type="error"
        text={error || ''}
        closeSnackbar={() => dispatch(geoLocationsActions.errorCleared())}
      />
    </div>
  );
};

export default GeoLocationsPage;
